import { FrontendToolHandler } from '../../types'
import { createSuccessResult, createErrorResult } from '../../types'
import { validateChartSpec } from './validateChartSpec'

/**
 * `create_visualization` is a **rendering** frontend tool: it performs no
 * side-effect. Its value is its arguments (the `ChartSpec`), which the chat
 * bubble renders via `ChartRenderer`. `execute` only validates the spec so the
 * reasoning trace reflects success/failure; the actual chart mounts in
 * `ChatBubble` from the streamed tool-call arguments.
 */
export const createVisualization: FrontendToolHandler = {
  definition: {
    name: 'create_visualization',
    description:
      'Render quantitative data as an interactive chart. Call this whenever your answer reports ' +
      '3 or more real numbers worth comparing, ranking, trending, or breaking down — e.g. ' +
      'metrics by category, a time series, a top-N ranking, or a distribution, including ' +
      'numbers you gathered via search. Pick the fitting type: line for trends, bar for ' +
      'comparisons, pie/donut for parts of a whole. Do not chart qualitative or narrative ' +
      'answers (news briefs, explanations, how-tos, opinions), a single figure, or data you do ' +
      'not actually have — never invent numbers just to justify a chart.',
    parameters: {
      type: 'object',
      required: ['type', 'series'],
      properties: {
        type: {
          type: 'string',
          enum: ['line', 'bar', 'area', 'pie', 'donut', 'scatter', 'radar', 'heatmap'],
          description: 'Chart type best suited to the data.'
        },
        title: { type: 'string' },
        subtitle: { type: 'string' },
        categories: {
          type: 'array',
          items: { type: 'string' },
          description: 'X-axis / slice labels, index-aligned to series data.'
        },
        series: {
          type: 'array',
          minItems: 1,
          items: {
            type: 'object',
            required: ['name', 'data'],
            properties: {
              name: { type: 'string' },
              data: { type: 'array', items: { type: 'number' } }
            }
          }
        },
        xAxisLabel: { type: 'string' },
        yAxisLabel: { type: 'string' },
        stacked: { type: 'boolean' }
      }
    },
    requiresConfirmation: false
  },
  execute: async (args) => {
    const result = validateChartSpec(args)
    return result.ok
      ? createSuccessResult('Chart rendered', result.spec)
      : createErrorResult(result.error)
  }
}
